import React, { useDeferredValue } from "react";
import { useParams } from "react-router";
import { Button, Callout, Page, QueryResult } from "@sscs/ui";
import { SaveIcon } from "@sscs/ui/icons";
import { capitalizeFirstLetter } from "@sscs/utils";
import { AllowedTo } from "Components";
import { useAppSelector, useDialog } from "Hooks";
import ConfigureLevel from "../components/ConfigureLevel/ConfigureLevel";
import PolicyTable from "../components/PolicyTable/PolicyTable";
import SavePolicy from "../components/SavePolicy/SavePolicy";
import {
  useConfigurationProvider,
  useDerivedScanLevel,
  useUnsavedChanges,
} from "../hooks";
import { ConfigurationProvider } from "../providers/ConfigurationProvider";
import {
  selectAutoApprovalEnabled,
  selectHasUnsavedChanges,
  selectOverrides,
  selectRevertOverrides,
  selectScanLevel,
} from "../store/policyConfigSlice";
import { PolicyConfigurationScope } from "../types";
import styles from "./policyConfiguration.module.scss";

const ConfigurationContent = (): React.JSX.Element => {
  const { groupId } = useParams();
  const hasUnsavedChanges = useAppSelector(selectHasUnsavedChanges);
  const overrides = useAppSelector(selectOverrides);

  const policyConfig = useConfigurationProvider();
  const deferredPolicyConfig = useDeferredValue(policyConfig);
  const {
    configScope,
    entityName,
    reprocessEstimate,
    permissions,
    status,
    updatePolicy,
  } = policyConfig;

  const revertOverrides = useAppSelector(selectRevertOverrides);
  const scanLevel = useAppSelector(selectScanLevel);
  const autoApprovalEnabled = useAppSelector(selectAutoApprovalEnabled);

  const saveDialog = useDialog();

  const { clearLevel, isDirty } = useDerivedScanLevel();

  useUnsavedChanges();

  const handleSave = ({
    notify_on_completion,
    reprocess_affected,
  }: {
    notify_on_completion: boolean;
    reprocess_affected: boolean;
  }) => {
    updatePolicy({
      notify_on_completion,
      reprocess_affected,
      overrides,
      revert_overrides: revertOverrides,
      rl_scan_level: scanLevel !== null ? scanLevel : undefined,
      ...(configScope === "group"
        ? { auto_approval_enabled: autoApprovalEnabled }
        : {}),
    });
  };

  return (
    <>
      <Page.Root>
        <Page.Header
          sticky
          title={`${capitalizeFirstLetter(configScope)} policy configuration: ${entityName}`}
          details={[
            isDirty && (
              <Button
                key="undo"
                variant="text"
                size="small"
                onClick={clearLevel}
              >
                Undo level change
              </Button>
            ),
          ]}
          actions={
            <div className={styles["save-toolbar"]}>
              {hasUnsavedChanges && (
                <Callout
                  type="error"
                  title="You have unsaved changes"
                  variant="inline"
                />
              )}
              <AllowedTo permissions={permissions} groupId={groupId}>
                <Button
                  color="primary"
                  onClick={saveDialog.open}
                  disabled={!hasUnsavedChanges || status.status !== "SUCCESS"}
                >
                  <SaveIcon className={styles.icon} />
                  Save
                </Button>
              </AllowedTo>
            </div>
          }
        />
        <Page.Body>
          <QueryResult status={status}>
            <div className={styles.stack}>
              <ConfigureLevel scope={configScope} status={status} />
              <PolicyTable policyConfig={deferredPolicyConfig} />
            </div>
          </QueryResult>
        </Page.Body>
      </Page.Root>
      <SavePolicy
        isOpen={saveDialog.isOpen}
        onClose={saveDialog.close}
        handleSave={handleSave}
        reprocessEstimate={reprocessEstimate}
      />
    </>
  );
};

type PolicyConfigurationProps = {
  configScope: PolicyConfigurationScope;
};

const PolicyConfiguration = ({
  configScope,
}: PolicyConfigurationProps): React.JSX.Element => {
  return (
    <ConfigurationProvider configScope={configScope}>
      <ConfigurationContent />
    </ConfigurationProvider>
  );
};

export default PolicyConfiguration;
